import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSeats, useCustomerInfo, useExistingReservations, useSeatValidation, SeatValidationNotification } from "../theater";
import { SeatMap, ReservationSummary, CustomerForm } from "../theater";
import { Toast, AdminHeader, SalesConfirmationModal } from "../../shared/components";
import ReturnReasonModal from "../../shared/components/ReturnReasonModal";
import { useAppSelector } from "../../shared/hooks";
import { theaterReservationService } from "../../shared/services";
import { eventService } from "../../shared/services";
import reservationService from "../../shared/services/reservationService";
import { buildYearOptions, getEventYear, getEffectiveEventStatus, getCurrentYear } from "../../shared/utils/eventStatus";

const TicketSalesDashboard = ({ adminData, onLogout }) => {
  const navigate = useNavigate();
  
  // Memoize Redux selectors to prevent unnecessary re-renders
  const requireAccessCode = useAppSelector(state => state.config.requireAccessCode);
  
  // Estados para el flujo de venta de entradas
  const [currentStep, setCurrentStep] = useState('selection'); // 'selection', 'seatmap', 'customer', 'summary'
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Calcular valores después de que selectedEvent esté definido
  const isAdmin = adminData?.role === 'admin';
  const maxTicketsPerCode = isAdmin ? Infinity : (selectedEvent?.maxTicketsPerCode || 6);
  const openSale = selectedEvent?.isOpenSale || false;
  const [accessCodeData, setAccessCodeData] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'success' });
  const [isCreatingReservation, setIsCreatingReservation] = useState(false);
  const [isFreeTicket, setIsFreeTicket] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [usedCountByCode] = useState(0);
  const [freeTicketAccessCode, setFreeTicketAccessCode] = useState('');
  const [freeTicketReason, setFreeTicketReason] = useState('');
  const [freeTicketAccessCodeValid, setFreeTicketAccessCodeValid] = useState(false);
  const [validatingFreeTicketAccessCode, setValidatingFreeTicketAccessCode] = useState(false);
  const [studentInfoFromAccessCode, setStudentInfoFromAccessCode] = useState(null);
  
  // Estados para el modal de motivo de devolución
  const [showReturnReasonModal, setShowReturnReasonModal] = useState(false);
  const [ticketToReturn, setTicketToReturn] = useState(null);
  
  // Estados para el modal de confirmación de venta
  const [showSalesConfirmationModal, setShowSalesConfirmationModal] = useState(false);
  
  // Estados para manejo de reservas (reutilizados de TheaterReservation)
  const [isModifyingReservations, setIsModifyingReservations] = useState(false);
  const [reservationsToCancel, setReservationsToCancel] = useState([]);
  const [reservationsToReturn, setReservationsToReturn] = useState([]);

  // Hook para validación de asientos
  const {
    validationError,
    recommendations,
    showValidationError,
    clearValidationError,
    showRecommendations,
    clearRecommendations,
    selectRecommendedSeat
  } = useSeatValidation();

  // Derivar información de usuario para prellenar CustomerForm
  const initialUserData = useMemo(() => {
    if (studentInfoFromAccessCode) {
      return studentInfoFromAccessCode;
    }
    if (accessCodeData?.userData) {
      return {
        name: accessCodeData.userData.name,
        phone: accessCodeData.userData.phone,
      };
    }
    return null;
  }, [accessCodeData, studentInfoFromAccessCode]);

  // Opciones de año y eventos visibles según el año elegido.
  // Se descartan además los eventos cuya fecha ya pasó aunque su estado guardado
  // siga en "próximo".
  const yearOptions = useMemo(() => buildYearOptions(events), [events]);
  const visibleEvents = useMemo(
    () =>
      events.filter(
        (event) =>
          getEffectiveEventStatus(event) === 'upcoming' &&
          (selectedYear === 'all' || String(getEventYear(event)) === String(selectedYear)),
      ),
    [events, selectedYear],
  );

  // Hook para manejar información del cliente
  const {
    customerInfo,
    updateCustomerInfo,
    isFormValid: baseIsFormValid,
    resetForm,
  } = useCustomerInfo(initialUserData);

  // Hook para manejar reservas existentes
  const userStudentId = accessCodeData?.userData?.id || null;
  const {
    existingReservations,
    getTotalExistingSeats,
    loadExistingReservations,
    getExistingSeatIds,
    hasActiveReservations,
  } = useExistingReservations(userStudentId, selectedEvent?.id, true); // true = contexto admin

  // Hook para manejar asientos
  const {
    plantaBajaSeats,
    primerPisoSeats,
    selectedSeats,
    setSelectedSeats,
    totalPrice,
    handleSeatClick: baseHandleSeatClick,
    getSeatClassName,
    effectiveSeatCount,
    palcoCapacity,
    canSelectMore,
    allSeats,
    loadOccupiedSeats,
    clearSelectedSeats,
    setSeatDeselectedCallback,
    setSeatReselectedCallback,

    seatTypesLoading,
    seatTypesError,
    createDefaults,
    seatTypeList,
    seatTypePriceMap,
    validateSeatSelection,
    getRecommendedSeats,
  } = useSeats(maxTicketsPerCode, selectedEvent?.id, reservationsToCancel, false, existingReservations, reservationsToReturn, openSale, isFreeTicket, isAdmin);

  // Función de logout que maneja la redirección
  const handleLogout = useCallback(() => {
    // Limpiar estados locales si es necesario
    setSelectedEvent(null);
    setAccessCodeData(null);
    setSelectedSeats([]);
    setIsFreeTicket(false);
    setIsBlocked(false);
    setIsModifyingReservations(false);
    setReservationsToCancel([]);
    setReservationsToReturn([]);
    
    // Resetear el formulario del cliente
    resetForm();
    
    // Llamar a la función de logout del componente padre
    if (onLogout) {
      onLogout();
    }
    
    // Redirigir al login del admin
    navigate('/admin/login');
  }, [onLogout, navigate, resetForm, setSelectedSeats]);

  // Validación personalizada para el formulario que incluye validaciones de entrada gratuita
  const isFormValid = useCallback(() => {
    // Validación base del formulario
    if (!baseIsFormValid()) {
      return false;
    }

    // Si es entrada gratuita, validar código de acceso y motivo
    if (isFreeTicket) {
      if (!freeTicketAccessCode.trim() || !freeTicketAccessCodeValid) {
        return false;
      }
      if (!freeTicketReason.trim()) {
        return false;
      }
    }

    return true;
  }, [baseIsFormValid, isFreeTicket, freeTicketAccessCode, freeTicketAccessCodeValid, freeTicketReason]);

  // Wrapper para handleSeatClick con logging
  const handleSeatClick = useCallback((seatId) => {
    baseHandleSeatClick(seatId);
  }, [baseHandleSeatClick]);

  // Mostrar toast
  const showToast = useCallback((message, type = 'success') => {
    setToast({ isVisible: true, message, type });
    setTimeout(() => setToast(prev => ({ ...prev, isVisible: false })), 5000);
  }, []);

  // Cargar eventos
  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await eventService.getActiveEvents();
      setEvents(response);
    } catch (err) {
      setError(err.message);
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (!requireAccessCode || adminData?.role === 'admin') {
      loadEvents();
    }
  }, [requireAccessCode, adminData?.role, loadEvents]);

  // Recomendaciones
  useEffect(() => {
    if (selectedSeats.length > 1) {
      const recs = getRecommendedSeats(selectedSeats);
      if (recs.length > 0) showRecommendations(recs); else clearRecommendations();
    } else {
      clearRecommendations();
    }
  }, [selectedSeats, getRecommendedSeats, showRecommendations, clearRecommendations]);

  // Callback global de error de validación
  useEffect(() => {
    window.showSeatValidationError = showValidationError;
    return () => { delete window.showSeatValidationError; };
  }, [showValidationError]);

  // Función para manejar la selección de asiento recomendado
  const handleSelectRecommendedSeat = useCallback((seatId) => {
    selectRecommendedSeat(seatId, handleSeatClick);
  }, [selectRecommendedSeat, handleSeatClick]);

  const handleEventSelect = (event) => {
    setSelectedEvent(event);
    setCurrentStep('seatmap');
  };

  const handleAccessCodeSearch = async (code) => {
    setLoading(true);
    setError(null);
    try {
      const validationResult = await theaterReservationService.validateAccessCode(code);
      if (!validationResult.valid) throw new Error(validationResult.message || 'Código de acceso no válido');

      if (validationResult.access_token) {
        localStorage.setItem('theaterAccessCodeToken', validationResult.access_token);
        localStorage.setItem('theaterUserCode', validationResult.code || code);
      }
      if (validationResult.userData) {
        localStorage.setItem('theaterUserData', JSON.stringify(validationResult.userData));
      }
      if (validationResult.eventData) {
        localStorage.setItem('theaterEventData', JSON.stringify(validationResult.eventData));
      }

      if (!validationResult.eventData) throw new Error('No se encontró información del evento para este código');
      
      const normalizedEvent = {
        id: validationResult.eventData.id,
        title: validationResult.eventData.title,
        eventDate: validationResult.eventData.date,
        eventTime: validationResult.eventData.time,
        isOpenSale: validationResult.eventData.isOpenSale,
        maxTicketsPerCode: validationResult.eventData.maxTicketsPerCode
      };
      
      setAccessCodeData(validationResult);
      setSelectedEvent(normalizedEvent);
      setCurrentStep('seatmap');
      showToast('Código de acceso válido', 'success');

      // Cargar reservas existentes del alumno (si aplica)
      await loadExistingReservations();

      // Autocompletar información del cliente si hay datos del estudiante
      if (validationResult.userData) {
        autoFillCustomerInfo(validationResult);
      }
    } catch (err) {
      setError(err.message);
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Función para autocompletar información del cliente desde el código de acceso
  const autoFillCustomerInfo = useCallback((accessCode) => {
    if (accessCode?.userData) {
      const studentInfo = {
        name: accessCode.userData.name,
        phone: accessCode.userData.phone,
      };
      setStudentInfoFromAccessCode(studentInfo);
      showToast('Información del cliente autocompletada desde el código de acceso', 'success');
    }
  }, [showToast]);

  // Validar código de acceso para entrada gratuita
  const validateFreeTicketAccessCode = async (code) => {
    if (!code.trim() || !selectedEvent?.id) return;
    
    setValidatingFreeTicketAccessCode(true);
    try {
      const validationResult = await theaterReservationService.validateAccessCode(code);
      
      if (!validationResult.valid) {
        setFreeTicketAccessCodeValid(false);
        showToast('Código de acceso no válido', 'error');
        return;
      }

      // Verificar que el código de acceso corresponda al evento seleccionado
      if (validationResult.eventData && validationResult.eventData.id === selectedEvent.id) {
        setFreeTicketAccessCodeValid(true);
        showToast('Código de acceso válido para entrada gratuita', 'success');
        
        // Guardar los datos del accessCode para usar en la creación de la reserva
        setAccessCodeData(validationResult);
        
        // Si hay datos del estudiante, autocompletar el formulario
        if (validationResult.userData) {
          autoFillCustomerInfo(validationResult);
        }
      } else {
        setFreeTicketAccessCodeValid(false);
        showToast('El código de acceso no corresponde al evento seleccionado', 'error');
      }
    } catch (err) {
      setFreeTicketAccessCodeValid(false);
      showToast(err.message || 'Error al validar código de acceso', 'error');
    } finally {
      setValidatingFreeTicketAccessCode(false);
    }
  };

  // Configurar callbacks para deselección/reselección de asientos cuando se modifica
  const handleSeatDeselected = useCallback((seatId) => {
    if (isModifyingReservations) {
      const reservation = existingReservations.find(r => r.compositeId === seatId);
      
      if (reservation) {
        if (reservation.status === 'sold' || reservation.status === 'delivered') {
          // Si es una reserva vendida o entregada, abrir modal para motivo de devolución
          // El asiento ya se deseleccionó en useSeats, si se cancela se reseleccionará
          setTicketToReturn(reservation);
          setShowReturnReasonModal(true);
        } else if (reservation.status === 'pending') {
          // Si es una reserva pendiente, agregarla a la lista de cancelaciones
          setReservationsToCancel(prev => {
            const exists = prev.find(r => r.id === reservation.id);
            return exists ? prev : [...prev, reservation];
          });
        }
      }
    }
  }, [isModifyingReservations, existingReservations]);

  const handleSeatReselected = useCallback((seatId) => {
    if (isModifyingReservations) {
      const reservation = existingReservations.find(r => r.compositeId === seatId);
      if (reservation) {
        if (reservation.status === 'sold' || reservation.status === 'delivered') {
          // Remover de la lista de devoluciones
          setReservationsToReturn(prev => prev.filter(r => r.id !== reservation.id));
        } else if (reservation.status === 'pending') {
          // Remover de la lista de cancelaciones
          setReservationsToCancel(prev => prev.filter(r => r.id !== reservation.id));
        }
      }
    }
  }, [isModifyingReservations, existingReservations]);

  // Función para confirmar la devolución con motivo
  const handleConfirmReturn = useCallback((reason) => {
    if (ticketToReturn) {
      // Agregar la reserva a la lista de devoluciones con el motivo
      setReservationsToReturn(prev => {
        const exists = prev.find(r => r.id === ticketToReturn.id);
        if (!exists) {
          return [...prev, { ...ticketToReturn, returnReason: reason }];
        }
        return prev;
      });
      
      // El asiento ya está deseleccionado, no necesitamos deseleccionarlo de nuevo
      // Solo agregarlo a la lista de devoluciones
      
      // Cerrar el modal
      setShowReturnReasonModal(false);
      setTicketToReturn(null);
      
      showToast('Entrada marcada para devolución', 'success');
    }
  }, [ticketToReturn, showToast]);

  // Función para cancelar el modal de devolución
  const handleCancelReturn = useCallback(() => {
    // Si hay un ticket que se iba a devolver, reseleccionar el asiento
    // porque el hook useSeats ya lo deseleccionó cuando se llamó al callback
    if (ticketToReturn) {
      // Usar setSelectedSeats directamente para restaurar el asiento
      setSelectedSeats(prev => {
        if (!prev.includes(ticketToReturn.compositeId)) {
          return [...prev, ticketToReturn.compositeId];
        }
        return prev;
      });
    }
    setShowReturnReasonModal(false);
    setTicketToReturn(null);
  }, [ticketToReturn, setSelectedSeats]);

  useEffect(() => {
    setSeatDeselectedCallback && setSeatDeselectedCallback(handleSeatDeselected, existingReservations);
  }, [setSeatDeselectedCallback, handleSeatDeselected, existingReservations]);

  useEffect(() => {
    setSeatReselectedCallback && setSeatReselectedCallback(handleSeatReselected);
  }, [setSeatReselectedCallback, handleSeatReselected]);

  // Preseleccionar reservas existentes si hay activas
  useEffect(() => {
    if (existingReservations.length > 0 && hasActiveReservations && hasActiveReservations() && !isModifyingReservations) {
      const existingSeatIds = getExistingSeatIds();
      setSelectedSeats(existingSeatIds);
      setIsModifyingReservations(true);
      setReservationsToCancel([]);
      setReservationsToReturn([]);
    }
  }, [existingReservations.length, hasActiveReservations, getExistingSeatIds, setSelectedSeats, isModifyingReservations]);

  // El hook useCustomerInfo ya maneja la actualización automática cuando initialUserData cambia
  // No necesitamos un useEffect adicional aquí

  const usedCount = useMemo(() => {
    const byStudent = getTotalExistingSeats();
    if (byStudent > 0) return byStudent;
    return usedCountByCode;
  }, [getTotalExistingSeats, usedCountByCode]);

  // const totalLimit = useMemo(() => maxTicketsPerCode, [maxTicketsPerCode]);

  // Calcular precio total efectivo considerando devoluciones y nuevas selecciones
  const effectiveTotalPrice = useMemo(() => {
    let total = 0;
    
    // Si estamos modificando reservas, sumar asientos nuevos Y reservas pendientes existentes
    if (isModifyingReservations && existingReservations.length > 0) {
      const existingSeatIds = getExistingSeatIds();
      const newSeats = selectedSeats.filter(seatId => !existingSeatIds.includes(seatId));
      
      // Sumar asientos nuevos
      newSeats.forEach(seatId => {
        let seat = allSeats.find(s => s.compositeId === seatId);
        if (!seat) {
          seat = allSeats.find(s => s.id === seatId);
        }
        
        if (seat) {
          const seatPrice = parseFloat(seat.price) || 0;
          if (seatPrice > 0) {
            total += seatPrice;
          }
        }
      });
      
      // Sumar reservas pendientes existentes que no estén marcadas para cancelación
      existingReservations.forEach(reservation => {
        const isMarkedForCancel = reservationsToCancel.some(r => r.id === reservation.id);
        const isMarkedForReturn = reservationsToReturn.some(r => r.id === reservation.id);
        
        // Si es una reserva pendiente y no está marcada para cancelación, incluirla en el total
        if (reservation.status === 'pending' && !isMarkedForCancel && reservation.price) {
          const reservationPrice = parseFloat(reservation.price) || 0;
          if (reservationPrice > 0) {
            total += reservationPrice;
          }
        }
        
        // Si la reserva está marcada para devolución y era 'sold' o 'delivered', restar su precio
        if (isMarkedForReturn && (reservation.status === 'sold' || reservation.status === 'delivered') && reservation.price) {
          const reservationPrice = parseFloat(reservation.price) || 0;
          if (reservationPrice > 0) {
            total -= reservationPrice;
          }
        }
      });
    } else {
      // Para ventas normales (no modificaciones), sumar todos los asientos seleccionados
      selectedSeats.forEach(seatId => {
        let seat = allSeats.find(s => s.compositeId === seatId);
        if (!seat) {
          seat = allSeats.find(s => s.id === seatId);
        }
        
        if (seat) {
          const seatPrice = parseFloat(seat.price) || 0;
          if (seatPrice > 0) {
            total += seatPrice;
          }
        }
      });
    }
    
    return total;
  }, [selectedSeats, allSeats, isModifyingReservations, existingReservations, reservationsToReturn, reservationsToCancel, getExistingSeatIds]);

  const handleCreateOrModify = async () => {
    // Si estamos modificando: procesar cancelaciones, devoluciones y crear diferencias
    const existingSeatIds = getExistingSeatIds();
    const seatsToCreate = selectedSeats.filter(seatId => !existingSeatIds.includes(seatId));
    const reservationsToCancelIds = reservationsToCancel.map(r => r.id);
    const reservationsToReturnIds = reservationsToReturn.map(r => r.id);

    // Verificar si hay cambios reales para procesar
    if (reservationsToCancelIds.length === 0 && reservationsToReturnIds.length === 0 && seatsToCreate.length === 0) {
      showToast('No hay cambios para procesar. Las entradas ya vendidas se mantienen sin modificar.', 'info');
      return true; // No hay cambios, pero no es un error
    }

    // Validación final de asientos antes de operar
    if (selectedSeats.length > 1) {
      const validation = validateSeatSelection(seatsToCreate);
      if (!validation.isValid) {
        showToast(validation.message, 'error');
        return false;
      }
    }

    // SOLUCIÓN: Usar el método unificado del backend para mantener el mismo operationId
    try {
      // Obtener el studentId de las reservas existentes que se están modificando
      let studentId = null;
      if (existingReservations.length > 0) {
        // Buscar una reserva que tenga studentId
        const reservationWithStudent = existingReservations.find(r => r.studentId);
        if (reservationWithStudent) {
          studentId = reservationWithStudent.studentId;
        }
      }

      // Preparar datos para el método unificado
      const modifyData = {
        eventId: selectedEvent.id,
        studentId: studentId, // Usar el studentId de las reservas existentes
        accessCodeId: accessCodeData?.code?.id || null,
        newSeats: seatsToCreate.map(seatId => ({ compositeId: seatId })),
        reservationsToCancel: reservationsToCancelIds,
        reservationsToReturn: reservationsToReturnIds.map(id => {
          const reservation = reservationsToReturn.find(r => r.id === id);
          return {
            id,
            reason: reservation?.returnReason || 'Modificación de reserva en venta de entradas'
          };
        }),
        status: 'sold', // Nuevas reservas se marcan directamente como vendidas
        notes: `Venta por modificación - Cliente: ${customerInfo.name}`,
        isFreeTicket,
        customerInfo: {
          name: customerInfo.name,
          phone: customerInfo.phone,
        }
      };
      // Llamar al método unificado del backend
      await reservationService.modifyExistingReservations(modifyData);
      
      showToast('Operación completada exitosamente con un solo operationId', 'success');
      return true;
      
    } catch (error) {
      showToast(`Error al procesar la operación: ${error.message}`, 'error');
      return false;
    }
  };

  const handleCompleteReservation = async () => {
    // Mostrar modal de confirmación primero
    setShowSalesConfirmationModal(true);
  };

      const handleConfirmSale = async () => {
    
    if (!selectedEvent?.id) {
      showToast('Error: Faltan datos para completar la venta', 'error');
      return;
    }

    // Validar que si es entrada gratuita, tenga código de acceso válido
    if (isFreeTicket && (!freeTicketAccessCode.trim() || !freeTicketAccessCodeValid)) {
      showToast('Para entradas gratuitas debe ingresar un código de acceso válido', 'error');
      return;
    }

    // Validar que si es entrada gratuita, tenga motivo
    if (isFreeTicket && !freeTicketReason.trim()) {
      showToast('Para entradas gratuitas debe ingresar un motivo', 'error');
      return;
    }

    setIsCreatingReservation(true);
    setShowSalesConfirmationModal(false);

    try {
      // Verificar si hay reservas pendientes que necesitan ser vendidas
      const hasPendingReservations = existingReservations.some(r => r.status === 'pending');
      
      if (isModifyingReservations && hasPendingReservations) {
        // Usar el método unificado para manejar cancelaciones y nuevas reservas correctamente
        const ok = await handleCreateOrModify();
        if (!ok) return;
        
        showToast('¡Venta completada! Reservas procesadas correctamente.', 'success');
        await loadOccupiedSeats();
        await loadExistingReservations();
        setIsModifyingReservations(false);
        setReservationsToCancel([]);
        setReservationsToReturn([]);
        clearSelectedSeats();
        resetForm();
      } else if (isModifyingReservations) {
        const ok = await handleCreateOrModify();
        if (!ok) return;
        // NO es necesario llamar a markGroupAsSold aquí porque modifyExistingReservations ya marca como sold
        showToast('¡Venta confirmada! Reservas marcadas como vendidas.', 'success');
        await loadOccupiedSeats();
        await loadExistingReservations();
        setIsModifyingReservations(false);
        setReservationsToCancel([]);
        clearSelectedSeats();
        resetForm();
      } else if (isBlocked) {
        // Bloqueo de asientos
        const seatsData = selectedSeats.map(seatId => ({ compositeId: seatId }));
        const reservationData = {
          eventId: selectedEvent.id,
          accessCodeId: null,
          seats: seatsData,
          status: 'blocked',
          notes: `Bloqueo de entradas por ${adminData?.name || 'Administrador'}`,
          isFreeTicket: true,
        };
        const created = await theaterReservationService.createTicketSale(reservationData);
        showToast(`Se bloquearon ${created.length} asiento(s)`, 'success');
        await loadOccupiedSeats();
        clearSelectedSeats();
      } else {
        // Crear venta normal o entrada gratuita
        const seatsData = selectedSeats.map(seatId => ({ compositeId: seatId }));
        
        // Determinar el status y studentId basado en el tipo de entrada
        let status = 'pending';
        let studentId = null;
        
        if (isFreeTicket) {
          // Para entradas gratuitas, usar status 'sold' y obtener studentId del accessCode
          status = 'sold';
          if (freeTicketAccessCodeValid && accessCodeData?.code?.id) {
            // Buscar el studentId del accessCode validado
            try {
              // Priorizar studentInfoFromAccessCode si está disponible
              if (studentInfoFromAccessCode?.id) {
                studentId = studentInfoFromAccessCode.id;
              } else if (accessCodeData?.userData?.id) {
                studentId = accessCodeData.userData.id;
              } else if (accessCodeData?.code?.studentId) {
                // Si el accessCode tiene studentId directamente
                studentId = accessCodeData.code.studentId;
              }
            } catch (e) {
              console.warn('No se pudo obtener studentId del accessCode:', e);
            }
          }
        }
        
        const reservationData = {
          eventId: selectedEvent.id,
          studentId: studentId,
          accessCodeId: accessCodeData?.code?.id || null,
          seats: seatsData,
          status: status,
          notes: isFreeTicket 
            ? `Entrada gratuita por ${adminData?.name || 'Administrador'} - Cliente: ${customerInfo.name} - Motivo: ${freeTicketReason}`
            : `Venta de entrada por ${adminData?.name || 'Administrador'} - Cliente: ${customerInfo.name}`,
          isFreeTicket, // Este flag le dice al backend que establezca price = 0
          customerInfo: {
            name: customerInfo.name,
            phone: customerInfo.phone,
          }
        };
        
        const createdReservations = await theaterReservationService.createTicketSale(reservationData);
        
        // Para entradas gratuitas, marcar como vendidas inmediatamente
        if (isFreeTicket && accessCodeData?.code?.id) {
          try {
            await reservationService.markGroupAsSold(accessCodeData.code.id);
          } catch (e) {
            console.warn('No se pudo marcar como vendido el grupo:', e?.message || e);
          }
        }
        
        // Para ventas normales con accessCode, también marcar como vendidas
        if (!isFreeTicket && accessCodeData?.code?.id) {
          try {
            await reservationService.markGroupAsSold(accessCodeData.code.id);
          } catch (e) {
            console.warn('No se pudo marcar como vendido el grupo:', e?.message || e);
          }
        }
        
        const successMessage = isFreeTicket 
          ? `¡Entrada gratuita creada exitosamente! Entradas: ${createdReservations.length} (Precio: $0)`
          : `¡Venta completada y marcada como vendida! Entradas: ${createdReservations.length}`;
          
        showToast(successMessage, 'success');
        await loadOccupiedSeats();
        resetTicketForm();
        clearSelectedSeats();
      }
    } catch (error) {
      showToast(error.message || 'Error al procesar la venta. Inténtalo de nuevo.', 'error');
    } finally {
      setIsCreatingReservation(false);
    }
  };

  const handleBlockSeats = async () => {
    // Mostrar modal de confirmación primero
    setShowSalesConfirmationModal(true);
  };

  const handleConfirmBlockSeats = async () => {
    if (!selectedEvent?.id || selectedSeats.length === 0) {
      showToast('Selecciona al menos un asiento', 'error');
      return;
    }

    if (selectedSeats.length > 1) {
      const validation = validateSeatSelection(selectedSeats);
      if (!validation.isValid) {
        showToast(validation.message, 'error');
        return;
      }
    }

    setIsCreatingReservation(true);
    setShowSalesConfirmationModal(false);
    
    try {
      const seatsData = selectedSeats.map(seatId => ({ compositeId: seatId }));
      const reservationData = {
        eventId: selectedEvent.id,
        accessCodeId: null,
        seats: seatsData,
        status: 'blocked',
        notes: `Bloqueo de entradas por ${adminData?.name || 'Administrador'}`,
        isFreeTicket: true,
      };
      const created = await theaterReservationService.createTicketSale(reservationData);
      showToast(`Se bloquearon ${created.length} asiento(s)`, 'success');
      await loadOccupiedSeats();
      clearSelectedSeats();
    } catch (error) {
      console.error('Error al bloquear asientos:', error);
      showToast(error.message || 'Error al bloquear asientos', 'error');
    } finally {
      setIsCreatingReservation(false);
    }
  };

  const resetTicketForm = () => {
    setSelectedEvent(null);
    setAccessCodeData(null);
    setSelectedSeats([]);
    resetForm();
    setIsFreeTicket(false);
    setIsBlocked(false);
    setIsModifyingReservations(false);
    setReservationsToCancel([]);
    setReservationsToReturn([]);
    setFreeTicketAccessCode('');
    setFreeTicketReason('');
    setFreeTicketAccessCodeValid(false);
    setStudentInfoFromAccessCode(null);
    setCurrentStep('selection');
    
    // Limpiar estados del modal de devolución
    setShowReturnReasonModal(false);
    setTicketToReturn(null);
  };

  const handleCancelModification = () => {
    setIsModifyingReservations(false);
    setReservationsToCancel([]);
    setReservationsToReturn([]);
    clearSelectedSeats();
    showToast('Modificación cancelada. Las reservas originales se mantienen.', 'info');
  };

  const goBack = () => {
    switch (currentStep) {
      case 'selection':
        navigate('/admin');
        break;
      case 'seatmap':
        setCurrentStep('selection');
        setSelectedEvent(null);
        setAccessCodeData(null);
        clearSelectedSeats();
        break;
      case 'customer':
        setCurrentStep('seatmap');
        clearSelectedSeats();
        break;
      case 'summary':
        setCurrentStep('customer');
        break;
      default:
        break;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'selection':
        if (adminData?.role === 'admin') {
          return (
            <div className="max-w-md mx-auto">
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Año</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                    >
                      <option value="all">Todos los años</option>
                      {yearOptions.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Evento</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onChange={(e) => {
                        const event = events.find(ev => ev.id === e.target.value);
                        if (event) handleEventSelect(event);
                      }}
                      value=""
                    >
                      <option value="">Selecciona un evento</option>
                      {visibleEvents.map(event => (
                        <option key={event.id} value={event.id}>
                          {event.title} - {new Date(event.eventDate).toLocaleDateString()} {event.eventTime}
                        </option>
                      ))}
                    </select>
                  </div>
                  {loading && (
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="text-gray-600 mt-2">Cargando eventos...</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }
        
        if (requireAccessCode) {
          return (
            <div className="max-w-md mx-auto">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4">Buscar por Código de Acceso</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Código de Acceso</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Ingresa el código de acceso"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAccessCodeSearch(e.target.value);
                        }
                      }}
                    />
                  </div>
                  <button
                    onClick={() => handleAccessCodeSearch(document.querySelector('input').value)}
                    disabled={loading}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Buscando...' : 'Buscar'}
                  </button>
                  {error && (<div className="text-red-600 text-sm">{error}</div>)}
                </div>
              </div>
            </div>
          );
        } else {
          return (
            <div className="max-w-md mx-auto">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4">Seleccionar Evento</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Año</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                    >
                      <option value="all">Todos los años</option>
                      {yearOptions.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Evento</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onChange={(e) => {
                        const event = events.find(ev => ev.id === e.target.value);
                        if (event) handleEventSelect(event);
                      }}
                      value=""
                    >
                      <option value="">Selecciona un evento</option>
                      {visibleEvents.map(event => (
                        <option key={event.id} value={event.id}>
                          {event.title} - {new Date(event.eventDate).toLocaleDateString()} {event.eventTime}
                        </option>
                      ))}
                    </select>
                  </div>
                  {loading && (
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="text-gray-600 mt-2">Cargando eventos...</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }

      case 'seatmap':
        if (seatTypesLoading) {
          return (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Cargando tipos de asientos...</p>
              </div>
            </div>
          );
        }

        if (seatTypesError) {
          return (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="text-red-600 mb-4">
                  <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <p className="text-gray-600 mb-4">Error al cargar tipos de asientos</p>
                <button onClick={createDefaults} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">Crear Tipos por Defecto</button>
              </div>
            </div>
          );
        }

        return (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Mapa de asientos */}
            <div className="lg:col-span-3">
              {/* Mensaje de modificación activa */}
              {isModifyingReservations && hasActiveReservations && hasActiveReservations() && (
                <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center text-blue-700 text-sm">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Tus reservas existentes están seleccionadas. Puedes modificar tu selección.
                  </div>
                  
                  {/* Mostrar resumen de cambios */}
                  {(reservationsToCancel.length > 0 || reservationsToReturn.length > 0) && (
                    <div className="mt-2 text-xs">
                      <div className="flex flex-wrap gap-2">
                        {reservationsToCancel.length > 0 && (
                          <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                            {reservationsToCancel.length} asiento(s) a cancelar
                          </span>
                        )}
                        {reservationsToReturn.length > 0 && (
                          <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded">
                            {reservationsToReturn.length} asiento(s) a devolver
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <SeatMap
                allSeats={allSeats}
                plantaBajaSeats={plantaBajaSeats}
                primerPisoSeats={primerPisoSeats}
                handleSeatClick={handleSeatClick}
                getSeatClassName={getSeatClassName}
                selectedSeats={selectedSeats}
                reservationsToCancel={reservationsToCancel}
                maxTicketsPerCode={maxTicketsPerCode}
                seatTypeList={seatTypeList}
                seatTypePriceMap={seatTypePriceMap}
              />
            </div>

            {/* Panel lateral derecho */}
            <div className="lg:col-span-1 space-y-6">
              <ReservationSummary
                selectedSeats={selectedSeats}
                totalPrice={effectiveTotalPrice}
                maxTicketsPerCode={maxTicketsPerCode}
                canSelectMore={canSelectMore}
                allSeats={allSeats}
                effectiveSeatCount={effectiveSeatCount}
                palcoCapacity={palcoCapacity}
                isModifyingReservations={isModifyingReservations}
                existingReservations={existingReservations}
                reservationsToCancel={reservationsToCancel}
                reservationsToReturn={reservationsToReturn}
                isOpenSale={openSale}
                isAdmin={isAdmin}
                onCancelModification={handleCancelModification}
                usedCount={usedCount}
              />

              {/* Opciones admin */}
              {adminData && adminData?.role === 'admin' && (
                <div className="bg-white rounded-lg shadow-md p-4">
                  <h3 className="text-lg font-semibold mb-3">Opciones de Administrador</h3>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="freeTicket"
                      checked={isFreeTicket}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setIsFreeTicket(checked);
                        if (checked) {
                          setIsBlocked(false);
                        } else {
                          // Limpiar valores cuando se desmarca
                          setFreeTicketAccessCode('');
                          setFreeTicketReason('');
                          setFreeTicketAccessCodeValid(false);
                        }
                      }}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <label htmlFor="freeTicket" className="text-sm font-medium text-gray-700">Entrada sin costo</label>
                  </div>

                  {/* Campos para entrada gratuita */}
                  {isFreeTicket && (
                    <div className="mt-3 space-y-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Código de Acceso *
                        </label>
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            value={freeTicketAccessCode}
                            onChange={(e) => setFreeTicketAccessCode(e.target.value)}
                            placeholder="Ingrese código de acceso"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        {freeTicketAccessCodeValid && (
                          <p className="text-xs text-green-600 mt-1">✓ Código válido para este evento</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Motivo *
                        </label>
                        <textarea
                          value={freeTicketReason}
                          onChange={(e) => setFreeTicketReason(e.target.value)}
                          placeholder="Ingrese el motivo de la entrada gratuita"
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div className="flex  mt-2">
                          <button
                            onClick={() => validateFreeTicketAccessCode(freeTicketAccessCode)}
                            disabled={!freeTicketAccessCode.trim() || validatingFreeTicketAccessCode}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
                          >
                            {validatingFreeTicketAccessCode ? 'Validando...' : 'Validar'}
                          </button>
                        </div>
                    </div>
                  )}
                  <div className="flex items-center space-x-2 mt-3">
                    <input
                      type="checkbox"
                      id="blockTicket"
                      checked={isBlocked}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setIsBlocked(checked);
                        if (checked) setIsFreeTicket(false);
                      }}
                      className="w-4 h-4 text-amber-600 bg-gray-100 border-gray-300 rounded focus:ring-amber-500 focus:ring-2"
                    />
                    <label htmlFor="blockTicket" className="text-sm font-medium text-gray-700">Bloquear entradas</label>
                  </div>
                  {isBlocked && (
                    <button
                      onClick={handleBlockSeats}
                      disabled={isCreatingReservation || selectedSeats.length === 0}
                      className="mt-4 w-full bg-amber-600 text-white px-4 py-2 rounded hover:bg-amber-700 disabled:opacity-50"
                    >
                      {isCreatingReservation ? 'Procesando...' : 'Bloquear entradas'}
                    </button>
                  )}
                  {isFreeTicket && (
                    <p className="text-xs text-gray-500 mt-1">
                      Esta entrada será marcada como gratuita. Debe ingresar código de acceso válido y motivo.
                    </p>
                  )}
                  {isBlocked && (<p className="text-xs text-gray-500 mt-1">Se crearán reservas bloqueadas sin costo</p>)}
                </div>
              )}

              {/* Formulario del cliente */}
              {(selectedSeats.length > 0 || isModifyingReservations) && !isBlocked && (
                <CustomerForm
                  customerInfo={customerInfo}
                  updateCustomerInfo={updateCustomerInfo}
                  isFormValid={isFormValid}
                  onCompleteReservation={handleCompleteReservation}
                  isCreatingReservation={isCreatingReservation}
                  isModifyingReservations={isModifyingReservations}
                  submitLabel="Vender entrada"
                  studentInfoFromAccessCode={studentInfoFromAccessCode}
                />
              )}
            </div>
          </div>
        );

      case 'customer':
        return (
          <div className="max-w-md mx-auto">
            <CustomerForm
              customerInfo={customerInfo}
              updateCustomerInfo={updateCustomerInfo}
              isFormValid={isFormValid}
              onCompleteReservation={handleCompleteReservation}
              isCreatingReservation={isCreatingReservation}
              isModifyingReservations={isModifyingReservations}
              studentInfoFromAccessCode={studentInfoFromAccessCode}
            />
          </div>
        );

      case 'summary':
        return (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Resumen de la Venta</h2>
              
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-2">Evento</h3>
                <p className="text-gray-700">{selectedEvent?.title}</p>
                <p className="text-gray-600">{selectedEvent?.eventDate && new Date(selectedEvent.eventDate).toLocaleDateString()} {selectedEvent?.eventTime}</p>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-medium mb-2">Asientos Seleccionados</h3>
                <div className="grid grid-cols-2 gap-2">
                  {selectedSeats.map(seatId => {
                    const seat = allSeats.find(s => s.compositeId === seatId);
                    return (
                      <div key={seatId} className="flex justify-between">
                        <span>{seat?.name || seatId}</span>
                        <span className="font-medium">
                          {isFreeTicket ? 'Gratis' : `$${seat?.price || 0}`}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between font-semibold">
                    <span>Total:</span>
                    <span>${isFreeTicket ? 0 : totalPrice}</span>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-medium mb-2">Información del Cliente</h3>
                <p><strong>Nombre:</strong> {customerInfo.name}</p>
                <p><strong>Teléfono:</strong> {customerInfo.phone}</p>
              </div>

              <div className="flex space-x-4">
                <button onClick={goBack} className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-md hover:bg-gray-600">Volver</button>
                <button onClick={handleCompleteReservation} disabled={isCreatingReservation} className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50">{isCreatingReservation ? 'Procesando...' : 'Confirmar Venta'}</button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader
        adminData={adminData}
        onLogout={handleLogout}
        showBackButton={true}
        onBackClick={() => navigate('/admin')}
        backButtonText="Volver al Dashboard"
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            {currentStep !== 'selection' && (
              <button onClick={goBack} className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 flex items-center">← Volver</button>
            )}
          </div>
          {renderStepContent()}
        </div>
      </main>
      
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
      />

      <SeatValidationNotification
        validationError={validationError}
        recommendations={recommendations}
        onClearError={clearValidationError}
        onClearRecommendations={clearRecommendations}
        onSelectRecommendedSeat={handleSelectRecommendedSeat}
      />

      {/* Modal para motivo de devolución */}
      <ReturnReasonModal
        isOpen={showReturnReasonModal}
        onClose={handleCancelReturn}
        onConfirm={handleConfirmReturn}
        ticketInfo={ticketToReturn}
      />

      {/* Modal de confirmación de venta */}
      <SalesConfirmationModal
        isOpen={showSalesConfirmationModal}
        onClose={() => setShowSalesConfirmationModal(false)}
        onConfirm={isBlocked ? handleConfirmBlockSeats : handleConfirmSale}
        isCreatingReservation={isCreatingReservation}
        selectedEvent={selectedEvent}
        selectedSeats={selectedSeats}
        allSeats={allSeats}
        customerInfo={customerInfo}
        totalPrice={effectiveTotalPrice}
        isFreeTicket={isFreeTicket}
        freeTicketReason={freeTicketReason}
        freeTicketAccessCode={freeTicketAccessCode}
        isModifyingReservations={isModifyingReservations}
        existingReservations={existingReservations}
        reservationsToCancel={reservationsToCancel}
        reservationsToReturn={reservationsToReturn}
        isBlocked={isBlocked}
      />
    </div>
  );
};

export default TicketSalesDashboard; 